import type { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { FlagshipCarouselBlock as FlagshipCarouselBlockValue } from "@reactivated";

import {
    FlagshipCarousel,
    ICarouselCell,
} from "../../apps/retail-locator/FlagshipCarousel";

/**
 * Native, value-based port of the FlagshipPage flagship ``CarouselBlock``
 * (renamed ``FlagshipCarouselBlock``, #33473), converted off the legacy
 * ``carousel_data`` JSON-string contract.
 *
 * The per-cell presentation data (image rendition + Wistia embed + label, in the
 * ``ICarouselCell`` shape) is computed server-side and surfaced as
 * ``value.serialized_cells`` (see ``FlagshipCarouselBlock.get_data_for_client_render``);
 * it is fed UNCHANGED to the existing interactive ``FlagshipCarousel`` component
 * (Flickity center-aligned carousel with Wistia video cells, hydrated via the
 * shared ``useFlickity`` hook), so the carousel behavior is preserved exactly.
 * The legacy ``<section role="region" aria-label=...>`` wrapper (formerly in
 * ``carousel.html``) moves into this component.
 */
export const FlagshipCarouselBlock = (
    props: Immutable<{ value: FlagshipCarouselBlockValue }>,
): React.JSX.Element => {
    // ``FlagshipCarousel`` is unchanged and types ``images`` as a mutable
    // ``ICarouselCell[]``; the reactivated payload is deeply readonly, so make a
    // shallow mutable copy of the (read-only) data for the call boundary.
    const images: ICarouselCell[] = props.value.serialized_cells.map(
        (cell) => ({
            ...cell,
        }),
    );
    return (
        <section role="region" aria-label={t`Tempur-Pedic Store Carousel`}>
            <FlagshipCarousel images={images} />
        </section>
    );
};
